import { useEffect, useState } from "react";
import { useMsal, useAccount } from "@azure/msal-react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLazyGetUserProfileDetailsQuery } from "common/redux/user/queries";
import useProfile from "./useProfile";
import useAuthMode from "./useAuthMode";
import { AuthMode } from "common/enums";
import useIsAuth0Instance from "./useIsAuth0Instance";
import { AuthService } from "common/services/auth.service";
import { AppStorageKey } from "common/models";
import { ENV_CONFIG } from "common/config/env.config";

const useAuthenticate = () => {
  const { isAuthenticated, authMode } = useAuthMode();
  const [getUserProfileDetails] = useLazyGetUserProfileDetailsQuery();
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const { accessStatus } = useProfile();
  const [isAuthReady, setIsAuthReady] = useState(false);
  useIsAuth0Instance(); // still called for side-effects (isAuthenticated, etc.)

  // Use the raw Auth0 getAccessTokenSilently directly — NOT the one from
  // useIsAuth0Instance, which may be overridden to AuthService.getToken
  // in MFE mode, creating a circular call:
  //   AuthService.getToken → fetcher → getAccessTokenSilently (=AuthService.getToken) → ♻️
  const { getAccessTokenSilently: auth0GetToken } = useAuth0();

  useEffect(() => {
    if (authMode === AuthMode.AUTH0 && auth0GetToken) {
      AuthService.setAuth0TokenFetcher(async () => {
        const token = await auth0GetToken();
        return token;
      });
    }
  }, [auth0GetToken, authMode]);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsAuthReady(false); // Reset `isAuthReady` when not authenticated
      return;
    }

    const existingTimestamp = localStorage.getItem(
      AppStorageKey.LOGIN_TIMESTAMP
    );
    const parsed = existingTimestamp ? parseInt(existingTimestamp, 10) : NaN;
    const hardSessionMs = ENV_CONFIG.USER_HARD_SESSION_TIMEOUT * 1000;
    const shouldReset =
      !existingTimestamp ||
      isNaN(parsed) ||
      Date.now() - parsed >= hardSessionMs;
    if (shouldReset) {
      localStorage.setItem(
        AppStorageKey.LOGIN_TIMESTAMP,
        Date.now().toString()
      );
    }

    switch (authMode) {
      case AuthMode.AUTH0:
      case AuthMode.KEYCLOAK:
        setIsAuthReady(true);
        getUserProfileDetails();
        break;
      case AuthMode.AAD:
        if (account) {
          instance.setActiveAccount(account);
          setIsAuthReady(true);
          getUserProfileDetails();
        }
        break;
      default:
        setIsAuthReady(false); // Handle unexpected auth modes
    }
  }, [isAuthenticated, authMode, account]);

  return { account, isAuthenticated, isAuthReady, accessStatus };
};

export default useAuthenticate;
